import { setCurrentPage, setItemsPerPage } from "../QualificationListSlice";
import { EmptyListNotice, Table } from "@hatch-digital-nl/abs-storybook/legacy";
import { useAppDispatch, useAppSelector } from "../../../app/hooks";
import { useGetQualificationsQuery } from "../../../common/api/qualification";
import Skeleton from "react-loading-skeleton";
import { mapQualificationDataToRow } from "../utils/mapQualificationDataToRow";
import ellipsisImage from "../../../common/images/ellipsis.svg";
import ellipsisWhiteImage from "../../../common/images/ellipsis-white.svg";
import { useNavigate } from "react-router";
import React, { JSX, useEffect } from "react";
import { useEstablishmentId } from "../../../common/utils/useEstablishmentId";
import { useIsEstablishmentManager } from "../../../common/utils/useIsEstablishmentManager";
import { getQualificationTableHeadColumns } from "../utils/getQualificationTableHeadColumns";
import {
    useGetEstablishmentByIdQuery,
    useGetEstablishmentStatisticsQuery,
} from "../../../common/api/establishment";
import { useEmployeeId } from "../../../common/utils/useEmployeeId";
import { getAchievedFilter } from "../../../common/utils/getAchievedFilter";
import { useGetEmployeeMeQuery } from "../../../common/api/employee";
import { QualificationTypeEnum } from "../../../common/enums/QualificationTypeEnum";

const QualificationListTable = (): JSX.Element | null => {
    const dispatch = useAppDispatch();
    const navigate = useNavigate();
    const establishmentId = useEstablishmentId();
    const employeeId = useEmployeeId();
    const isEstablishmentManager = useIsEstablishmentManager() ?? false;
    const {
        currentPage,
        searchTerm,
        itemsPerPage,
        itemsPerPageSelect,
        functionGroupFilter,
        qualificationStandardFilter,
        achievedFilter,
        sort,
    } = useAppSelector((state) => state.qualificationList);

    const { data: employeeMeData } = useGetEmployeeMeQuery({});

    const { data: establishmentData, isLoading: establishmentDataIsLoading } =
        useGetEstablishmentByIdQuery({
            id: establishmentId,
        });

    const {
        data: establishmentStatisticsData,
        isLoading: establishmentStatisticsDataIsLoading,
    } = useGetEstablishmentStatisticsQuery(
        { id: establishmentId },
        { skip: !isEstablishmentManager || !establishmentId },
    );

    const {
        data: qualificationsData,
        isLoading,
        isFetching,
    } = useGetQualificationsQuery({
        page: currentPage,
        items_per_page: itemsPerPage,
        sort: sort.sortName,
        filter: {
            search: searchTerm,
            ...(qualificationStandardFilter
                ? {
                      qualification_standard_id:
                          qualificationStandardFilter.value,
                  }
                : {}),
            ...(functionGroupFilter
                ? { function_group_id: functionGroupFilter.value }
                : {}),
            ...getAchievedFilter({
                achievedFilter,
                isEstablishmentManager,
                establishmentId,
                employeeId,
            }),
            ...(!isEstablishmentManager
                ? { type: QualificationTypeEnum.PERSON }
                : {}),
            ...(isEstablishmentManager && establishmentId
                ? { establishment_id: establishmentId }
                : {}),
        },
    });

    useEffect(() => {
        if (
            qualificationsData &&
            qualificationsData.data.length === 0 &&
            currentPage > 1
        ) {
            dispatch(setCurrentPage(1));
        }
    }, [qualificationsData, currentPage, dispatch]);

    if (
        isLoading ||
        isFetching ||
        establishmentDataIsLoading ||
        (isEstablishmentManager && establishmentStatisticsDataIsLoading)
    ) {
        return (
            <Skeleton
                height={"60rem"}
                borderRadius={"1.5rem"}
                style={{ marginTop: "3.2rem" }}
            />
        );
    }

    if (
        !qualificationsData?.data?.length ||
        !establishmentData ||
        !employeeMeData ||
        (isEstablishmentManager && !establishmentStatisticsData)
    ) {
        return (
            <EmptyListNotice content={"Er zijn geen kwalificaties gevonden"} />
        );
    }

    return (
        <section className={"p-qualification-list__table"}>
            <Table
                isPagination={true}
                data={{
                    columns: getQualificationTableHeadColumns({
                        isEstablishmentManager,
                        sort,
                        dispatch,
                    }),
                    rows: mapQualificationDataToRow({
                        qualificationsData: qualificationsData.data,
                        navigate,
                        dispatch,
                        isEstablishmentManager,
                        establishmentStatisticsData:
                            establishmentStatisticsData,
                        establishmentAchievedQualifications:
                            establishmentData.achieved_qualifications,
                        employeeAchievedQualifications:
                            employeeMeData.achieved_qualifications,
                    }),
                    total: qualificationsData.meta?.total ?? 0,
                }}
                itemsPerPage={qualificationsData.meta?.per_page ?? 0}
                itemsPerPageSelect={itemsPerPageSelect}
                onItemsPerPageChange={(
                    event: React.ChangeEvent<HTMLSelectElement>,
                ) => dispatch(setItemsPerPage(Number(event.target.value)))}
                currentPage={currentPage}
                onPaginationChange={(page: number) =>
                    dispatch(setCurrentPage(page))
                }
                iconButtonIconUrl={ellipsisImage}
                iconButtonBackgroundColor={"rgb(243, 244, 246)"}
                onOpenIconButtonUrl={ellipsisWhiteImage}
                onOpenIconButtonBackgroundColor={"#1D2D58"}
            />
        </section>
    );
};

export default QualificationListTable;
